import {CopyOutlined} from '@ant-design/icons'
import {Switch} from 'antd'
import {CampaignBean, ListResponseCampaignListItemBean} from 'common/api/api.ts'
import {campaignApi} from 'common/api/entitiesApi.tsx'
import {ActiveStatusSelector} from 'common/components/dictionary/ActiveStatusSelector.tsx'
import {GoToReportButton} from 'common/components/GoToReportButton.tsx'
import {ListActionButton} from 'common/components/ListActionButton.tsx'
import {ListCard} from 'common/components/ListCard'
import {AdvertiserSelector} from 'common/components/reloadable/AdvertiserSelector'
import {SearchInput} from 'common/components/SearchInput'
import {ListFilters, useAppContext} from 'common/context/AppContext.tsx'
import {COLUMN_ID_WIDTH, COLUMN_NAME_WIDTH} from 'common/util/constants'
import {initListFilters, useListFilters} from 'common/util/useListFilters.ts'
import {apiCall, Format, formatCompact} from 'common/util/Util.tsx'
import dayjs from 'dayjs'
import {useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import { CommonRoutes } from 'common/context/commonRoutes.ts'

const CampaignList = ({agency}) => {
    const navigateTo = useNavigate()
    const appContext = useAppContext()
    const [filters, setFilters] = useState<ListFilters>(initListFilters())
    const {setFilter} = useListFilters(setFilters)
    const [loading, setLoading] = useState<boolean>(false)
    const [listData, setListData] = useState<ListResponseCampaignListItemBean>()

    const loadData = () => {
        apiCall(
            setLoading,
            appContext,
            () =>
                campaignApi.campaignList(
                    filters.sort?.name,
                    filters.sort?.direction,
                    filters.name,
                    filters.active,
                    filters.advertiserId,
                    {query: {page: filters.page, size: filters.pageSize}}
                ),
            (data) => setListData(data)
        )
    }

    const toggleActive = (record: CampaignBean, checked: boolean) => {
        if (record.id) {
            const id = record.id
            apiCall(
                setLoading,
                appContext,
                () => (checked ? campaignApi.campaignActivate(id) : campaignApi.campaignDeactivate(id)),
                () => {
                    record.active = checked
                    setListData(listData)
                }
            )
        }
    }

    return (
        <ListCard
            loading={loading}
            title="Campaigns"
            createName="Campaign"
            createRoute={CommonRoutes.CAMPAIGN_CREATE}
            listRouteKey={CommonRoutes.CAMPAIGN}
            filters={filters}
            setFilters={setFilters}
            listData={listData}
            loadData={loadData}
            columns={[
                {
                    title: 'ID',
                    dataIndex: 'id',
                    width: COLUMN_ID_WIDTH,
                    fixed: 'left'
                },
                {
                    title: 'Name',
                    dataIndex: 'name',
                    className: 'list-table-name',
                    sorter: true,
                    ellipsis: true,
                    render: (text, record) => (
                        <Link style={{color: '#5888CF', minWidth: 100}} to={`${CommonRoutes.CAMPAIGN}/${record.id}/edit`}>
                            {text}
                        </Link>
                    ),
                    width: COLUMN_NAME_WIDTH,
                    fixed: 'left'
                },
                {
                    title: 'Ad Groups',
                    dataIndex: 'adGroups',
                    width: 70,
                    render: (value, record) => (
                        <Link to={`${CommonRoutes.AD_GROUP}?campaignId=${record.id}`} style={{color: '#5888CF'}}>
                            {value}
                        </Link>
                    )
                },
                ...(agency
                    ? [
                        {
                            title: 'Advertiser',
                            dataIndex: 'advertiserName',
                            sorter: true,
                            ellipsis: true,
                            width: 120
                        }
                    ]
                    : []),
                {
                    title: 'Today (cost/budget)',
                    dataIndex: 'dailyBudget',
                    sorter: true,
                    width: 130,
                    render: (_, record) => (
                        <>
                            {formatCompact(Format.Cost, record.dailyCost)} /
                            <span style={{color: '#9FA3A9'}}> {formatCompact(Format.Cost, record.dailyBudget)}</span>
                        </>
                    )
                },
                {
                    title: 'Total (cost/budget)',
                    dataIndex: 'totalBudget',
                    sorter: true,
                    width: 130,
                    render: (_, record) => (
                        <>
                            {formatCompact(Format.Cost, record.totalCost)} /
                            <span style={{color: '#9FA3A9'}}> {formatCompact(Format.Cost, record.totalBudget)}</span>
                        </>
                    )
                },
                {
                    title: 'Time Zone',
                    dataIndex: 'timezone',
                    sorter: false,
                    width: 60,
                },
                {
                    title: 'Start Date',
                    dataIndex: 'startDate',
                    sorter: true,
                    width: 80,
                    render: (date) => date && dayjs(date).format('DD MMM YY')
                },
                {
                    title: 'End Date',
                    dataIndex: 'endDate',
                    sorter: true,
                    width: 80,
                    render: (date) => date && dayjs(date).format('DD MMM YY')
                },
                {
                    title: 'Days Left',
                    dataIndex: 'daysLeft',
                    sorter: true,
                    width: 70
                },
                {
                    title: 'Last Modified',
                    dataIndex: 'modified',
                    sorter: true,
                    width: 100,
                    render: (date) => date && dayjs(date).format('DD MMM YY')
                },
                {
                    title: 'Active',
                    dataIndex: 'active',
                    sorter: true,
                    render: (val, record: CampaignBean) => (
                        <Switch
                            size="small"
                            checked={record.active}
                            onChange={(checked) => record.id && toggleActive(record, checked)}
                        />
                    ),
                    width: 60
                },
                {
                    title: 'Actions',
                    render: (_, record: CampaignBean) => (
                        <div style={{display: 'flex', gap: 8, justifyContent: 'center'}}>
                            <ListActionButton onClick={() => navigateTo(`${CommonRoutes.CAMPAIGN}/${record.id}/copy`)} tooltip="Duplicate">
                                <CopyOutlined/>
                            </ListActionButton>
                            <GoToReportButton title="Report" filterName="CampaignId" id={record.id}/>
                        </div>
                    ),
                    width: 100
                }
            ]}
        >
            <SearchInput value={filters.name} onChange={(value) => setFilter('name', value)}/>
            {agency && (
                <AdvertiserSelector
                    placeholder="Filter by Advertiser"
                    style={{minWidth: '200px'}}
                    value={filters.advertiserId}
                    onChange={(value: number) => setFilter('advertiserId', value)}
                />
            )}
            <ActiveStatusSelector
                placeholder="Filter by Active"
                style={{minWidth: '100px'}}
                value={filters.active}
                onChange={(value) => setFilter('active', value)}
            />
        </ListCard>
    )
}

export default CampaignList
